<script setup lang="ts">
import VuePdfEmbed, { type VuePdfEmbedMethods } from 'vue-pdf-embed';
import { pdfSource } from '@/utils/pdf-source.util';
import { computed, ref, toRef, watch } from 'vue';
import { useDisplay } from 'vuetify';

import { type ResumeUrlSigner, useSignedResumeUrl } from '@/composables/useSignedResumeUrl';
import { CustomerSuccessService } from '@/core/customer-success/customer-success.service';
import { DialogService } from '@/core/shared/dialog/dialog.service';
import { EmployerFeatureGatingService } from '@/core/shared/employer-feature-gating/employer-feature-gating.service';
import SubscriptionService from '@/core/shared/subscription/subscription.service';
import { SnackbarService } from '@/core/shared/snackbar/snackbar.service';
import { ErrorService } from '@/core/shared/errors/error.service';
import SpinnerLoader from '@/components/Shared/Loaders/SpinnerLoader.vue';
import { FeatureName } from '@factoryfixinc/ats-interfaces';
import { toPdfFileName } from '@/utils/strings/pdf-file-name.util';

const props = defineProps<{
  modelValue: boolean;
  proName: string;
  resumeUrl?: string;
  resumeSigner?: ResumeUrlSigner;
  userProfileId?: number;
  // Opt-in: only Talent Search wants this action in the resume header today.
  showAddToJob?: boolean;
  addToJobDisabled?: boolean;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void;
  (e: 'rendering-failed'): void;
  (e: 'loading-failed'): void;
  (e: 'add-to-job'): void;
}>();

const isLoading = ref(true);
const fileRef = ref<VuePdfEmbedMethods | null>(null);

const display = useDisplay();
// Fullscreen edge-to-edge on mobile; the PDF is scaled to fit the viewport width.
const isMobile = computed(() => display.smAndDown.value);

const customerSuccessService = new CustomerSuccessService();
const dialogService = new DialogService();
const featureGatingService = new EmployerFeatureGatingService();
const subscriptionService = SubscriptionService.instance;

const isRequestingReport = ref(false);

const canRequestJobMatchReport = computed(
  () =>
    !!props.userProfileId &&
    featureGatingService.hasAccess(FeatureName.MARKETABLE_CANDIDATE_REPORTS) &&
    subscriptionService.isEmployerSubscribed,
);

async function requestJobMatchReport() {
  if (!props.userProfileId) return;
  isRequestingReport.value = true;
  try {
    const { successCount, batchId } = await customerSuccessService.submitJobMatchReport({
      candidates: [{ userProfileId: props.userProfileId }],
      resumes: [],
    });
    if (successCount === 0) {
      SnackbarService.critical('We could not queue your report. Please try again.');
      return;
    }
    emit('update:modelValue', false);
    dialogService.jobMatchReportSuccessModalProps = { isOpen: true, referenceId: batchId };
  } catch (error) {
    ErrorService.captureException(error);
    SnackbarService.critical('An unexpected error occurred. Please try again.');
  } finally {
    isRequestingReport.value = false;
  }
}

const { signedUrl, error: signedUrlError } = useSignedResumeUrl(
  toRef(props, 'resumeUrl'),
  props.resumeSigner,
  toRef(props, 'modelValue'),
);

watch(signedUrlError, (newError) => {
  if (newError) emit('loading-failed');
});

const resumeTitle = computed(() => {
  if (props.proName.endsWith('s')) {
    return `${props.proName}'s resume`;
  }

  return `${props.proName}'s resume`;
});

function downloadResume() {
  if (!fileRef.value) {
    return;
  }

  fileRef.value.download(toPdfFileName(resumeTitle.value));
}
</script>

<template>
  <v-dialog :model-value="modelValue" :fullscreen="isMobile" persistent>
    <div
      :class="
        isMobile
          ? 'flex h-full w-full flex-col bg-shade-900 p-3 pt-[max(0.75rem,env(safe-area-inset-top))]'
          : 'dialog-max-width mx-auto w-full'
      "
    >
      <!-- Close dialog icon (white, sits on the dark backdrop / dark mobile shell) -->
      <div class="flex shrink-0 justify-end">
        <div class="flex cursor-pointer py-1" @click="emit('update:modelValue', false)">
          <img src="@/assets/svg/close-white.svg" alt="Close" />
          <span class="ml-1 text-sm font-semibold text-white">Close</span>
        </div>
      </div>

      <!-- Dialog Window -->
      <div
        :class="[
          'mt-2 rounded-2xl bg-white px-6 py-2',
          isMobile ? 'flex min-h-0 flex-1 flex-col' : '',
        ]"
      >
        <!-- Dialog Header (stacks on mobile so the title + actions don't crowd) -->
        <div
          class="flex shrink-0 flex-col gap-2 md:h-14 md:flex-row md:items-center md:justify-between"
        >
          <div class="sensitive font-serif font-650">{{ resumeTitle }}</div>

          <!-- Wraps instead of overflowing: on narrow mobile widths, Request Job Match
               Report + Download + Add to Job together don't fit in one row. -->
          <div class="flex flex-wrap items-center justify-end gap-2">
            <button
              v-if="canRequestJobMatchReport"
              data-test="request-job-match-report"
              class="flex items-center rounded-md border border-shade-900 px-3 py-1.5 disabled:opacity-60"
              :disabled="isRequestingReport"
              @click="requestJobMatchReport"
            >
              <SpinnerLoader v-if="isRequestingReport" class="mr-1 h-[18px] w-[18px]" />
              <span class="text-sm font-semibold text-shade-900">Request Job Match Report</span>
            </button>

            <button
              :class="[
                'flex items-center rounded-md px-3 py-1.5',
                showAddToJob ? 'border border-shade-900' : 'bg-shade-900',
              ]"
              @click="downloadResume"
            >
              <img
                src="@/assets/svg/download-white.svg"
                :class="['mr-1 w-5', { invert: showAddToJob }]"
                alt="Download"
              />
              <span
                :class="['text-sm font-semibold', showAddToJob ? 'text-shade-900' : 'text-white']"
                >Download</span
              >
            </button>

            <button
              v-if="showAddToJob"
              data-test="add-to-job"
              class="flex items-center rounded-md bg-shade-900 px-3 py-1.5 text-white disabled:!bg-tint-80 disabled:!text-tint-400"
              :disabled="isRequestingReport || addToJobDisabled"
              @click="emit('add-to-job')"
            >
              <span class="text-sm font-semibold">Add to Job</span>
            </button>
          </div>
        </div>

        <!-- Resume -->
        <div
          v-if="props.resumeUrl || props.resumeSigner"
          :class="[
            'overflow-y-auto border border-tint-80',
            isMobile
              ? 'min-h-0 flex-1 pb-[env(safe-area-inset-bottom)]'
              : 'resume-max-height h-full',
          ]"
        >
          <v-skeleton-loader
            v-if="!signedUrlError && (isLoading || !signedUrl)"
            boilerplate
            type="heading, subtitle, paragraph@10"
          />

          <VuePdfEmbed
            v-if="signedUrl"
            :source="pdfSource(signedUrl)"
            ref="fileRef"
            class="sensitive"
            @rendered="isLoading = false"
            @loading-failed="emit('loading-failed')"
            @rendering-failed="emit('rendering-failed')"
          >
            <template #after-page>
              <div class="h-8 bg-tint-60 shadow-inner" />
            </template>
          </VuePdfEmbed>
        </div>
      </div>
    </div>
  </v-dialog>
</template>

<style scoped>
.dialog-max-width {
  max-width: 956px;
}

.resume-max-height {
  max-height: calc(100vh - 116px);
}

/* On mobile, scale PDF pages down to fit the viewport width (no horizontal scroll). */
@media (max-width: 767px) {
  :deep(canvas) {
    max-width: 100%;
    height: auto;
  }
}
</style>
